import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Grid, Input, Message, Modal } from '@arco-design/web-react';

import type { VendorDeviceBillOption, VendorDeviceSelection } from '../../components/vendorAndDeviceRadioList';
import {
  VendorAndDeviceRadioList,
  VendorDeviceSelectionRequiredField,
  findVendorDeviceOption,
  formatVendorDeviceCellLabel,
  hasVendorDeviceSelection,
  optionToSelection,
  resolveVendorDeviceRequiredValidateField,
} from '../../components/vendorAndDeviceRadioList';
import { VendorDevicePersistFields } from '../../components/vendorDevice';
import type { ModelSubscriptionProviderBlockProps, ModelSubscriptionProviderPaths } from './types';
import { buildMsProviderVendorDeviceFormPatch, readMsProviderVendorDevice } from './vendorDeviceMapping';

interface MsPricingProviderSelectorProps {
  form: ModelSubscriptionProviderBlockProps['form'];
  paths: ModelSubscriptionProviderPaths;
  billModelId: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  resourceCode: string;
  disabled: boolean;
}

/** 计价模式：供应商/模型输入框回填 + 弹窗选择（打开弹窗再请求，确认后写入） */
export function MsPricingProviderSelector({
  form,
  paths,
  billModelId,
  deploymentTypeId,
  resourceCode,
  disabled,
}: MsPricingProviderSelectorProps) {
  const { t } = useTranslation();
  const pricingValue = Form.useWatch(paths.pricing, form);
  const strategyName = Form.useWatch(paths.strategyName, form);
  const strategyDisplayName = Form.useWatch(paths.strategyDisplayName, form);
  const useSubResourcePricingWatch = Form.useWatch(paths.useSubResourcePricing, form);
  const requiredField = resolveVendorDeviceRequiredValidateField(paths.pricing);

  const [modalVisible, setModalVisible] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<VendorDeviceSelection | null>(null);
  const [modalOptions, setModalOptions] = useState<VendorDeviceBillOption[]>([]);

  const selection = useMemo(
    () =>
      readMsProviderVendorDevice(
        {
          [paths.pricing]: pricingValue,
          [paths.strategyName]: strategyName,
          [paths.strategyDisplayName]: strategyDisplayName,
          [paths.useSubResourcePricing]: useSubResourcePricingWatch,
        },
        paths,
      ),
    [paths, pricingValue, strategyDisplayName, strategyName, useSubResourcePricingWatch],
  );

  const vendorRaw = String(selection?.vendor ?? '').trim();
  const modelRaw = String(selection?.device ?? '').trim();
  const vendorDisplay = vendorRaw ? formatVendorDeviceCellLabel(vendorRaw) : '';
  const modelDisplay = modelRaw ? formatVendorDeviceCellLabel(modelRaw) : '';

  const readSelection = useCallback(
    () => readMsProviderVendorDevice(form.getFieldsValue() as Record<string, unknown>, paths),
    [form, paths],
  );

  const writeSelection = useCallback(
    (next: VendorDeviceSelection) => {
      const isSub = Boolean(next.useSubResourcePricing);
      form.setFieldsValue({
        ...buildMsProviderVendorDeviceFormPatch(next, paths),
        [paths.useSubResourcePricing]: isSub,
        ...(isSub ? { [paths.billingTotalTokens]: undefined } : { [paths.inputTokens]: undefined, [paths.outputTokens]: undefined }),
      });
      void form.validate([requiredField]).catch(() => undefined);
    },
    [form, paths, requiredField],
  );

  const openModal = () => {
    if (disabled) return;
    setPendingSelection(selection);
    setModalOptions([]);
    setModalVisible(true);
  };

  /** 列表加载后用完整 option 回填 pending（含 useSubResourcePricing），避免未重选直接确认丢失计价态 */
  const handleOptionsChange = useCallback(
    (options: VendorDeviceBillOption[]) => {
      setModalOptions(options);
      setPendingSelection((prev) => {
        const matched = findVendorDeviceOption(options, prev);
        if (matched) {
          form.setFieldValue(paths.useSubResourcePricing, optionToSelection(matched).useSubResourcePricing);
        }
        return matched ? optionToSelection(matched) : prev;
      });
    },
    [form, paths.useSubResourcePricing],
  );

  const handleConfirm = () => {
    const matched = findVendorDeviceOption(modalOptions, pendingSelection);
    const next = matched ? optionToSelection(matched) : pendingSelection;
    if (!next || !hasVendorDeviceSelection(next)) {
      Message.warning(t('data-center:model.subscription.provider.placeholder'));
      return;
    }
    writeSelection(next);
    setModalVisible(false);
  };

  const supplierLabel = t('data-center:model.subscription.supplier');
  const modelLabel = t('data-center:model.subscription.model');

  return (
    <>
      <VendorDevicePersistFields fieldPrefix={paths.pricing} />
      <VendorDeviceSelectionRequiredField fieldPrefix={paths.pricing} readSelection={readSelection} compactMessage={false}>
        <Grid.Row gutter={16}>
          <Grid.Col span={12}>
            <Form.Item label={supplierLabel} required style={{ marginBottom: 0 }}>
              <Input
                readOnly
                disabled={disabled}
                value={vendorDisplay}
                placeholder={t('common:select.placeholder.name', { name: supplierLabel })}
                onClick={openModal}
              />
            </Form.Item>
          </Grid.Col>
          <Grid.Col span={12}>
            <Form.Item label={modelLabel} required style={{ marginBottom: 0 }}>
              <Input
                readOnly
                disabled={disabled}
                value={modelDisplay}
                placeholder={t('common:select.placeholder.name', { name: modelLabel })}
                onClick={openModal}
              />
            </Form.Item>
          </Grid.Col>
        </Grid.Row>
      </VendorDeviceSelectionRequiredField>

      {modalVisible ? (
        <Modal
          title={t('data-center:model.subscription.provider.select.title')}
          visible
          maskClosable={false}
          style={{ width: '90vw', maxWidth: 1000, minWidth: 720 }}
          unmountOnExit
          onCancel={() => setModalVisible(false)}
          onOk={handleConfirm}
        >
          <VendorAndDeviceRadioList
            billModelId={billModelId}
            deploymentTypeId={deploymentTypeId}
            resourceCode={resourceCode}
            value={pendingSelection}
            disabled={false}
            skipFetch={false}
            hideTitle
            showSubResources={false}
            vendorLabel={supplierLabel}
            deviceLabel={modelLabel}
            onChange={setPendingSelection}
            onOptionsChange={handleOptionsChange}
          />
        </Modal>
      ) : null}
    </>
  );
}
